import { sleep } from "./util";
import { getAudioForPhonemes } from "./phonemeToAudio";
import {getUserData} from "Services/API";
import useIsMounted from "./hooks/useIsMounted";

export default function useConversation(speech, listen) {

  const isMountedRef = useIsMounted();

  async function speak(params) {
    if (!params) return;
    if (typeof params === "string") params = { textToSpeak: params };
    await speech.speakWithPromise({ ...params });
  }

  function speakSequence(sequenceToSpeak){
    // Filter out any undefined or null values in the sequence
    const cleansSequence = sequenceToSpeak.filter(item => item !== undefined && item !== null);
    if (cleansSequence.length === 0) return new Promise.resolve();

    let speechProps = {
      textToSpeak: cleansSequence.map(item => (typeof item === "string") ? { textToSpeak: item } : item),
      passThroughSpeechTags: true,
    }

    return new Promise((resolve) => {
      speech.speakSequence({ ...speechProps, onEnd: () => resolve()});
    });
  }

  function speakSlowly(params) {
    if (!params) return;
    if (typeof params === "string") params = { textToSpeak: params };
    params.textToSpeak = `<prosody rate=\"x-slow\">${params.textToSpeak}</prosody>`;
    return new Promise((resolve) => {
      speech.speak({ ...params, onEnd: () => resolve() });
    });
  }

  function speakFile(params) {
    if (typeof params === "string") params = { url: params };
    return new Promise((resolve) => {
      speech.speakAudioFile({ ...params, onfinish: () => resolve() });
    });
  }

  // phon: full phoneme string
  // onSpeak: fired before the sound is spoken. is passed iterator
  // delay: interval between speakFile events
  async function speakPhonemes(params) {
    const { locale } = getUserData();
    const { phon, onSpeak, delay = 0 } = params;
    const files = getAudioForPhonemes(phon, locale);
    for (let i = 0; i < files.length; i++) {
      if (onSpeak) onSpeak(i);
      const file = files[i];
      await speakFile(file);
      if (!isMountedRef.current) return;
      await sleep(delay);
    }
  }

  // phon: full phoneme string
  // onSpeak: fired before the syllable is spoken. is passed iterator
  // delay: interval between speak events
  async function speakSyllables(params) {
    const { phon, onSpeak, delay = 500 } = params;
    const syllables = phon.split("/");

    for (let i = 0; i < syllables.length; i++) {
      if (onSpeak) onSpeak(i);
      const syllable = syllables[i];
      const tts = `<phoneme alphabet="ipa" ph="${syllable}">xx</phoneme>`;
      await speak(tts);
      if (!isMountedRef.current) return;
      await sleep(delay);
    }
  }

  function listenPromise(params) {

    return new Promise((resolve) => {
      listen({
        ...params,
        speech,
        callback: (resp) => {
          const status = getStatus(resp);
          resolve({ ...resp, status });
        },
      });
    });
  }

  // options for patterOverride: match, struggle, silence, skip

  async function listenWord(params) {
    const { targetWord, patterOverride, interventionType, silencePatter } =
      params;

    // add silence to patterOverride if silencePatter is provided
    if (silencePatter && !patterOverride.includes("silence"))
      patterOverride.push("silence");

    const { status } = await listenPromise({
      targetWord,
      patterOverride,
      interventionType,
    });
    if (!isMountedRef.current) return;
    if (status === "silence" && silencePatter) {
      await speak(silencePatter);
      if (!isMountedRef.current) return;
      return await listenPromise({
        targetWord,
        patterOverride,
        interventionType,
      });
    } else return { status };
  }

  async function listenPhoneme({ targetWord }) {
    const patterOverride = ["match", "struggle", "silence"];
    let { status } = await listenPromise({
      patterOverride,
      phoneme: true,
      targetWord,
    });
    if (status !== "skip") status = "match"; // coerce to match unless skip
    return { status };
  }

  function stopListening() {
    if(speech.stopListening){
      speech.stopListening();
    }else{
      console.warn("speech.stopListening does not exist")
    }
  }

  function startListening() {
    if(speech.startListening){
      speech.startListening();
    }else{
      console.warn("speech.startListening does not exist")
    }
  }

  function abortSpeech() {
    return new Promise((resolve) => {
      speech.abortSpeech(() => {
        resolve();
      });
    });
  }

  return {
    speak,
    speakSequence,
    speakFile,
    speakPhonemes,
    speakSyllables,
    speakSlowly,
    listenWord,
    listenPhoneme,
    stopListening,
    startListening,
    abortSpeech
  };
}

function getStatus(resp) {
  const { matches } = resp;

  if (matches.targetMatches > 0) return "match";
  if (matches.moveOn) return "skip";
  if (resp.maxTimeElapsed) {
    if (resp.transcript) return "struggle";
    else return "silence";
  }

  return "";
}
